/**
 * Security Utilities for Password Validation and Input Sanitization
 */

/* ---------- Password Complexity Validation ---------- */

/**
 * Common weak passwords blacklist
 */
const WEAK_PASSWORDS = [
  "password",
  "password123",
  "12345",
  "123456",
  "1234567",
  "12345678",
  "123456789",
  "1234567890",
  "abc123",
  "qwerty",
  "qwerty123",
  "letmein",
  "welcome",
  "admin",
  "admin123",
  "test",
  "test123",
  "guest",
  "user",
  "root",
];

/**
 * Validate password complexity
 * Requirements:
 * - Minimum 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 * - Not in common password blacklist
 *
 * @param {string} password - Password to validate
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validatePasswordComplexity = (password) => {
  const errors = [];

  // Check minimum length
  if (!password || password.length < 12) {
    errors.push("Password must be at least 12 characters long");
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  // Check for number
  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  // Check for special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one special character (!@#$%^&*...)");
  }

  // Check against weak password list
  if (WEAK_PASSWORDS.includes(password.toLowerCase())) {
    errors.push("This password is too common. Please choose a stronger password");
  }

  // Check for sequential characters (e.g., "123456", "abcdef") - only flag if 4+ sequential
  if (/(?:abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmno|mnop|nopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz|0123|1234|2345|3456|4567|5678|6789)/i.test(password)) {
    errors.push("Password should not contain 4 or more sequential characters");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get password strength indicator
 * @param {string} password - Password to check
 * @returns {Object} { strength: string, score: number }
 */
export const getPasswordStrength = (password) => {
  let score = 0;

  if (!password) return { strength: "weak", score: 0 };

  // Length score
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 20) score += 1;

  // Character variety
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1;

  // Bonus for variety
  const charTypes = [
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  ].filter(Boolean).length;

  if (charTypes >= 4) score += 1;

  // Penalty for weak patterns
  if (WEAK_PASSWORDS.includes(password.toLowerCase())) score = 0;
  if (/(.)\1{2,}/.test(password)) score -= 1; // Repeated characters

  score = Math.max(0, Math.min(10, score));

  if (score <= 3) return { strength: "weak", score };
  if (score <= 6) return { strength: "medium", score };
  return { strength: "strong", score };
};

/* ---------- Enhanced Input Validation ---------- */

/**
 * Validate name fields (first name, last name)
 * - Alpha characters only
 * - Max 50 characters
 * - Min 2 characters
 * - No XSS patterns
 *
 * @param {string} name - Name to validate
 * @param {string} fieldName - Field name for error messages
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateName = (name, fieldName = "Name") => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return { isValid: false, error: `${fieldName} must be at least 2 characters` };
  }

  if (trimmedName.length > 50) {
    return { isValid: false, error: `${fieldName} must not exceed 50 characters` };
  }

  // Check for XSS patterns
  if (/<script|<iframe|javascript:|onerror=|onclick=/i.test(name)) {
    return { isValid: false, error: `${fieldName} contains invalid characters` };
  }

  // Allow only letters, spaces, hyphens, and apostrophes
  if (!/^[A-Za-z\s\-']+$/.test(trimmedName)) {
    return { isValid: false, error: `${fieldName} should contain only letters, spaces, hyphens, and apostrophes` };
  }

  return { isValid: true, error: "" };
};

/**
 * Validate passport number
 * - Alphanumeric only
 * - 8-12 characters
 * - No special characters
 *
 * @param {string} passport - Passport number to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePassport = (passport) => {
  if (!passport || passport.trim().length === 0) {
    return { isValid: false, error: "Passport number is required" };
  }

  const trimmedPassport = passport.trim().toUpperCase();

  if (trimmedPassport.length < 8 || trimmedPassport.length > 12) {
    return { isValid: false, error: "Passport number must be 8-12 characters" };
  }

  // Check for XSS patterns
  if (/<script|<iframe|javascript:|onerror=|onclick=/i.test(passport)) {
    return { isValid: false, error: "Passport number contains invalid characters" };
  }

  // Alphanumeric only
  if (!/^[A-Z0-9]+$/.test(trimmedPassport)) {
    return { isValid: false, error: "Passport number must be alphanumeric (letters and numbers only)" };
  }

  // Check for obviously fake passports (all same digit)
  if (/^(.)\1+$/.test(trimmedPassport)) {
    return { isValid: false, error: "Please enter a valid passport number" };
  }

  return { isValid: true, error: "" };
};

/**
 * Validate PNR (Passenger Name Record)
 * - Exactly 10 digits or 6 alphanumeric characters
 * - No special characters
 *
 * @param {string} pnr - PNR to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePNR = (pnr) => {
  if (!pnr || pnr.trim().length === 0) {
    return { isValid: false, error: "PNR is required" };
  }

  const trimmedPNR = pnr.trim().toUpperCase();

  // Check for XSS patterns
  if (/<script|<iframe|javascript:|onerror=|onclick=/i.test(pnr)) {
    return { isValid: false, error: "PNR contains invalid characters" };
  }

  // Check for valid PNR format (either 10 digits or 6 alphanumeric)
  const is10Digits = /^\d{10}$/.test(trimmedPNR);
  const is6Alphanumeric = /^[A-Z0-9]{6}$/.test(trimmedPNR);

  if (!is10Digits && !is6Alphanumeric) {
    return { isValid: false, error: "PNR must be either 10 digits or 6 alphanumeric characters" };
  }

  // Check for obviously fake PNRs (all zeros, all ones, etc.)
  if (/^(.)\1+$/.test(trimmedPNR)) {
    return { isValid: false, error: "Please enter a valid PNR" };
  }

  return { isValid: true, error: "" };
};

/**
 * Sanitize and validate input in real-time
 * @param {string} input - Input value
 * @param {string} type - Input type (name, passport, pnr, etc.)
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input, type) => {
  if (!input) return "";

  let sanitized = input;

  // First remove all HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, "");

  // Remove script tags and common XSS patterns (for any remaining encoded patterns)
  sanitized = sanitized.replace(/<script[^>]*>.*?<\/script>/gi, "");
  sanitized = sanitized.replace(/<iframe[^>]*>.*?<\/iframe>/gi, "");
  sanitized = sanitized.replace(/javascript:/gi, "");
  sanitized = sanitized.replace(/on\w+=/gi, "");

  switch (type) {
    case "name":
      // Allow only letters, spaces, hyphens, apostrophes
      sanitized = sanitized.replace(/[^A-Za-z\s\-']/g, "");
      break;
    case "passport":
      // Allow only alphanumeric, convert to uppercase
      sanitized = sanitized.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      break;
    case "pnr":
      // Allow only alphanumeric, convert to uppercase
      sanitized = sanitized.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      break;
    case "numeric":
      // Allow only digits
      sanitized = sanitized.replace(/[^0-9]/g, "");
      break;
    default:
      // Already removed HTML tags at the start
      break;
  }

  return sanitized;
};
